import { useState } from 'react'
import { useForm, useWatch, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { onboardingApi } from '../api/onboarding.api'
import { step2Schema, type Step2FormData, type ToneType, type PedagogyStyleType } from '../types'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { useTranslation } from 'react-i18next'

interface OnboardingStep2Props {
  onNext: () => void
  onBack: () => void
}

const TONES: { value: ToneType; labelKey: string; icon: string; descKey: string }[] = [
  { value: 'encouraging', labelKey: 'teaching.tones.encouraging', icon: '🌟', descKey: 'teaching.tones.encouraging_desc' },
  { value: 'strict', labelKey: 'teaching.tones.strict', icon: '📐', descKey: 'teaching.tones.strict_desc' },
  { value: 'playful', labelKey: 'teaching.tones.playful', icon: '🎮', descKey: 'teaching.tones.playful_desc' },
  { value: 'neutral', labelKey: 'teaching.tones.neutral', icon: '⚖️', descKey: 'teaching.tones.neutral_desc' },
]

const STYLES: { value: PedagogyStyleType; labelKey: string; icon: string; descKey: string }[] = [
  { value: 'socratic', labelKey: 'teaching.styles.socratic', icon: '💭', descKey: 'teaching.styles.socratic_desc' },
  { value: 'direct', labelKey: 'teaching.styles.direct', icon: '🎯', descKey: 'teaching.styles.direct_desc' },
  { value: 'collaborative', labelKey: 'teaching.styles.collaborative', icon: '🤝', descKey: 'teaching.styles.collaborative_desc' },
  { value: 'adaptive', labelKey: 'teaching.styles.adaptive', icon: '🔄', descKey: 'teaching.styles.adaptive_desc' },
]

function OptionButton<T extends string>({
  selected,
  label,
  icon,
  desc,
  onClick,
}: {
  value: T
  selected: boolean
  label: string
  icon: string
  desc: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={[
        'flex min-h-[52px] flex-col items-start gap-0.5 rounded-lg border-2 px-4 py-3 text-left transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-primary',
        selected
          ? 'border-mindeia-primary bg-mindeia-primary-light'
          : 'border-surface-200 bg-white hover:border-mindeia-primary hover:bg-mindeia-primary-subtle',
      ].join(' ')}
    >
      <div className="flex items-center gap-2">
        <span aria-hidden="true">{icon}</span>
        <span className={['text-sm font-semibold', selected ? 'text-mindeia-primary' : 'text-neutral-900'].join(' ')}>
          {label}
        </span>
      </div>
      <span className="text-xs text-neutral-600">{desc}</span>
    </button>
  )
}

export function OnboardingStep2({ onNext, onBack }: OnboardingStep2Props) {
  const { t } = useTranslation()
  const [apiError, setApiError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<Step2FormData>({
    resolver: zodResolver(step2Schema),
    defaultValues: { tone: 'encouraging', pedagogy_style: 'direct', description: '' },
  })

  const watchedName = useWatch({ control, name: 'persona_name' })
  const watchedTone = useWatch({ control, name: 'tone' })
  const watchedStyle = useWatch({ control, name: 'pedagogy_style' })

  const toneKey = TONES.find((tone) => tone.value === watchedTone)?.labelKey
  const styleKey = STYLES.find((style) => style.value === watchedStyle)?.labelKey

  const onSubmit = async (data: Step2FormData) => {
    setApiError(null)
    try {
      await onboardingApi.postStep2(data)
      onNext()
    } catch {
      setApiError(t('onboarding.error'))
    }
  }

  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 flex-col gap-5" noValidate>
        <div className="mb-2">
          <h2 className="text-xl font-semibold text-neutral-900">{t('onboarding.step2.title')}</h2>
          <p className="mt-1 text-sm text-neutral-600">{t('onboarding.step2.subtitle')}</p>
        </div>

        {apiError && <Alert variant="error">{apiError}</Alert>}

        <Input
          label={t('onboarding.step2.persona_name')}
          placeholder={t('onboarding.step2.persona_name_placeholder')}
          error={errors.persona_name?.message && t(errors.persona_name.message)}
          {...register('persona_name')}
        />

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-neutral-800">{t('onboarding.step2.tone')}</span>
          <Controller
            name="tone"
            control={control}
            render={({ field }) => (
              <div className="grid grid-cols-2 gap-2">
                {TONES.map((tone) => (
                  <OptionButton
                    key={tone.value}
                    value={tone.value}
                    selected={field.value === tone.value}
                    label={t(tone.labelKey)}
                    icon={tone.icon}
                    desc={t(tone.descKey)}
                    onClick={() => field.onChange(tone.value)}
                  />
                ))}
              </div>
            )}
          />
          {errors.tone && <p role="alert" className="text-xs text-mindeia-error">{t(errors.tone.message ?? '')}</p>}
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-neutral-800">{t('onboarding.step2.style')}</span>
          <Controller
            name="pedagogy_style"
            control={control}
            render={({ field }) => (
              <div className="grid grid-cols-2 gap-2">
                {STYLES.map((style) => (
                  <OptionButton
                    key={style.value}
                    value={style.value}
                    selected={field.value === style.value}
                    label={t(style.labelKey)}
                    icon={style.icon}
                    desc={t(style.descKey)}
                    onClick={() => field.onChange(style.value)}
                  />
                ))}
              </div>
            )}
          />
          {errors.pedagogy_style && (
            <p role="alert" className="text-xs text-mindeia-error">{t(errors.pedagogy_style.message ?? '')}</p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-neutral-800">
            {t('onboarding.step2.description')}{' '}
            <span className="text-neutral-600 font-normal">{t('onboarding.step2.optional')}</span>
          </label>
          <textarea
            {...register('description')}
            placeholder={t('onboarding.step2.description_placeholder')}
            rows={3}
            maxLength={500}
            className="rounded-md border border-surface-200 px-3 py-2 text-sm text-neutral-900 placeholder:text-surface-200 focus:border-mindeia-primary focus:outline-none focus:ring-2 focus:ring-mindeia-primary resize-none"
          />
          {errors.description && (
            <p role="alert" className="text-xs text-mindeia-error">{t(errors.description.message ?? '')}</p>
          )}
        </div>

        <div className="flex gap-3">
          <Button type="button" variant="secondary" size="lg" className="flex-1" onClick={onBack}>
            {t('onboarding.step2.back')}
          </Button>
          <Button type="submit" isLoading={isSubmitting} size="lg" className="flex-1">
            {t('onboarding.step2.submit')}
          </Button>
        </div>
      </form>

      {/* Live preview */}
      <aside className="hidden lg:flex lg:w-64 flex-col gap-3" aria-label={t('onboarding.step2.preview_label')}>
        <p className="text-sm font-medium text-neutral-600">{t('onboarding.step2.preview')}</p>
        <div className="rounded-xl border-2 border-mindeia-primary-light bg-mindeia-primary-subtle p-5 flex flex-col gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-mindeia-primary text-white text-xl font-bold">
            {watchedName ? watchedName[0]?.toUpperCase() : '?'}
          </div>
          <div>
            <p className="font-semibold text-neutral-900">
              {watchedName || t('onboarding.step2.preview_name_fallback')}
            </p>
            <p className="text-xs text-neutral-600 mt-0.5">{t('onboarding.step2.preview_role')}</p>
          </div>
          {(toneKey || styleKey) && (
            <div className="flex flex-wrap gap-1.5">
              {toneKey && (
                <span className="rounded-full bg-mindeia-primary px-2.5 py-0.5 text-xs font-medium text-white">
                  {t(toneKey)}
                </span>
              )}
              {styleKey && (
                <span className="rounded-full bg-mindeia-accent px-2.5 py-0.5 text-xs font-medium text-white">
                  {t(styleKey)}
                </span>
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
  )
}
